import {Routes} from '@angular/router';
import {MainComponent} from './main.component';
import {MaskSelectComponent} from './mask-select.component';
import {LoginComponent} from './core/login/login.component';
import {LogoutComponent} from './core/login/logout.component';
import {AdminGuard} from './core/guards/admin-guard.service';
import {LandingRedirectGuard} from './core/guards/landing-redirect-guard.service';
import {DashboardAccessGuard} from './core/guards/dashboard-access-guard.service';
import {PublisherGuard} from './core/guards/publisher-guard.service';
import {ForbiddenComponent} from './core/components/forbidden.component';
import {NetworkGuard} from './core/guards/network-guard.service';
import {AppRoutePath} from './app-route-path.enum';
import { TaxonomyGenresModule } from './modules/taxonomy/genres/taxonomy-genres.module';

export const appRoutes: Routes = [
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'logout',
    component: LogoutComponent
  },
  {
    path: 'p/:id',
    canLoad: [PublisherGuard],
    loadChildren: () => import('./modules/publisher/publisher.module').then(m => m.PublisherModule)
  },
  {
    path: 'select',
    component: MaskSelectComponent
  },
  {
    path: '',
    component: MainComponent,
    canActivate: [AdminGuard],
    canActivateChild: [NetworkGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [LandingRedirectGuard],
        // Guard always returns a UrlTree; component is never rendered.
        component: ForbiddenComponent,
      },
      {
        path: 'dashboard',
        canActivate: [DashboardAccessGuard],
        loadChildren: () => import('./modules/dashboard/dashboard 2.0/dashboard.module').then(m => m.DashboardModule),
      },
      {
        path: 'users',
        loadChildren: () => import('./modules/users/users.module').then(m => m.UsersModule),
      },
      {
        path: 'sessions',
        loadChildren: () => import('./modules/sessions/sessions.module').then(m => m.SessionsModule),
      },
      {
        path: 'publishers',
        loadChildren: () => import('./modules/publishers/publishers.module').then(m => m.PublishersModule),
      },
      {
        path: 'networks',
        loadChildren: () => import('./modules/networks/networks.module').then(m => m.NetworksModule),
      },
      {
        path: 'channels',
        loadChildren: () => import('./modules/channels/channels.module').then(m => m.ChannelsModule),
      },
      {
        path: 'owned-operated-apps',
        loadChildren: () => import('./modules/owned-operated-apps/apps.module').then(m => m.OwnedOperatedAppsModule),
      },
      {
        path: 'collections',
        loadChildren: () => import('./modules/playlists/playlists.module').then(m => m.PlaylistsModule),
      },
      {
        path: 'videos',
        loadChildren: () => import('./modules/videos/videos.module').then(m => m.VideosModule),
      },
      {
        path: 'placements',
        loadChildren: () => import('./modules/placements/placements.module').then(m => m.PlacementsModule),
      },
      {
        path: 'audio-supply',
        loadChildren: () => import('./modules/audio-supply/audio-supply.module').then(m => m.AudioSupplyModule),
      },
      {
        path: 'display-supply',
        loadChildren: () => import('./modules/display-supply/display-supply.module').then(m => m.DisplaySupplyModule),
      },
      {
        path: 'supply-search',
        loadChildren: () => import('./modules/supply-search/supply-search.module').then(m => m.SupplySearchModule),
      },
      {
        path: 'supply-upload',
        loadChildren: () => import('./modules/supply-upload/supply-upload.module').then(m => m.SupplyUploadModule),
      },
      {
        path: AppRoutePath.TARGETING,
        loadChildren: () => import('./modules/targeting/targeting.module').then(m => m.TargetingModule),
      },
      {
        path: AppRoutePath.BLOCK_LIST,
        loadChildren: () => import('./modules/block-list/block-list.module').then(m => m.BlockListModule),
      },
      {
        path: 'reports',
        loadChildren: () => import('./modules/reports/reports.module').then(m => m.ReportsModule),
      },
      {
        path: 'custom-reports',
        loadChildren: () => import('./modules/custom-reports/custom-reports.module').then(m => m.CustomReportsModule),
      },
      {
        path: 'scheduled-reports',
        loadChildren: () => import('./modules/scheduled-reports/scheduled-reports.module').then(m => m.ScheduledReportsModule),
      },
      {
        path: AppRoutePath.ERRORS,
        loadChildren: () => import('./modules/errors/errors.module').then(m => m.ErrorsModule),
      },
      {
        path: 'content-owner',
        loadChildren: () => import('./modules/content-owner/content-owner.module').then(m => m.ContentOwnerModule),
      },
      {
        path: 'demand',
        loadChildren: () => import('./modules/demand/demand.module').then(m => m.DemandModule),
      },
      {
        path: 'connections',
        loadChildren: () => import('./modules/connections/connections.module').then(m => m.ConnectionsModule),
      },
      {
        path: 'changelog',
        loadChildren: () => import('./modules/changelog/changelog.module').then(m => m.ChangelogModule),
      },
      {
        path: 'logs',
        loadChildren: () => import('./modules/system-logs/logs.module').then(m => m.LogsModule),
      },
      {
        path: 'debug-capture',
        loadChildren: () => import('./modules/debug-capture/debug-capture.module').then(m => m.DebugCaptureModule),
      },
      {
        path: 'ads-txt',
        loadChildren: () => import('./modules/ads-txt/ads-txt.module').then(m => m.AdsTxtModule),
      },
      {
        path: AppRoutePath.CAMPAIGNS,
        loadChildren: () => import('./modules/campaigns/campaigns.module').then(m => m.CampaignsModule),
      },
      {
        path: 'line-items',
        loadChildren: () => import('./modules/campaigns/line-items/line-item.modules').then(m => m.LineItemsModule),
      },
      {
        path: 'orders',
        loadChildren: () => import('./modules/campaigns/orders/orders.module').then(m => m.OrdersModule),
      },
      {
        path: 'apps',
        loadChildren: () => import('./modules/apps/apps.module').then(m => m.AppsModule),
      },
      {
        path: 'marketplace',
        loadChildren: () => import('./modules/marketplace/marketplace.module').then(m => m.MarketplaceModule),
      },
      {
        path: 'performance',
        loadChildren: () => import('./modules/performance/performance.module').then(m => m.PerformanceModule),
      },
      {
        path: 'assets',
        loadChildren: () => import('./modules/audio-to-video/assets/a2v-assets.module').then(m => m.A2vAssetsModule),
      },
      {
        path: 'brands',
        loadChildren: () => import('./modules/audio-to-video/brands/a2v-brands.module').then(m => m.A2vBrandsModule),
      },
      {
        path: 'network-template',
        loadChildren: () => import('./modules/audio-to-video/network-template/a2v-network-template.module').then(m => m.A2vNetworkTemplateModule),
      },
      {
        path: 'sources',
        loadChildren: () => import('./modules/moments/sources/sources.module').then(m => m.SourcesModule),
      },
      {
        path: 'supply-marketplace',
        loadChildren: () => import('./modules/supply-marketplace/supply-marketplace.module').then(m => m.SupplyMarketplaceModule),
      },
      // {
      //   path: 'marketplace-routing',
      //   loadChildren: () => import('./modules/marketplace-routing/marketplace-routing.module').then(m => m.MarketplaceRoutingModule),
      // },
      {
        path: 'forecast',
        loadChildren: () => import('./modules/forecast/forecast.module').then(m => m.ForecastModule),
      },
      {
        path: 'advertiser',
        loadChildren: () => import('./modules/campaigns/advertiser/advertiser.module').then(m => m.AdvertiserModule),
      },
      {
        path: 'deals',
        loadChildren: () => import('./modules/pmp-deals/pmp-deals.module').then(m => m.PmpDealsModule)
      },
      {
        path: 'taxonomy',
        loadChildren: () => import('./modules/taxonomy/genres/taxonomy-genres.module').then(m => m.TaxonomyGenresModule)
      },
    ]
  },
  {
    path: 'forbidden',
    component: ForbiddenComponent
  },
  {
    path: '**',
    redirectTo: ''
  }
];
